import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { invalidateRegistryLists } from '../../../../components/SharedRecord/registryResolveClient';
import { useAuth } from '../../../../contexts/authContext';
import { deliveryClient } from '../../../../lib/connect/clients';
import { registryRecordClient } from '../registryRecordClient';
import { dayToTimestamp } from './corporateEvents';
export const companyKeys = {
    dossier: (viewer, id)=>[
            'registry',
            'company',
            viewer,
            id,
            'dossier'
        ],
    rates: (viewer, id, vehicle)=>[
            'registry',
            'company',
            viewer,
            id,
            'rates',
            vehicle
        ],
    people: (viewer, id)=>[
            'registry',
            'company',
            viewer,
            id,
            'people'
        ],
    family: (viewer, id, asOf, includeEnded)=>[
            'registry',
            'company',
            viewer,
            id,
            'family',
            asOf,
            includeEnded
        ],
    links: (viewer, id)=>[
            'registry',
            'company',
            viewer,
            id,
            'links'
        ]
};
export function useCompany(id, vehicleId) {
    const { user } = useAuth();
    const viewer = `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
    const enabled = !!user?.tenant_id && !!user?.id && !!id;
    const dossier = useQuery({
        queryKey: companyKeys.dossier(viewer, id),
        enabled,
        retry: false,
        queryFn: async ()=>{
            try {
                return await registryRecordClient.getCompanyDossier({
                    companyId: id
                });
            } catch (error) {
                if (error instanceof ConnectError && [
                    Code.NotFound,
                    Code.PermissionDenied
                ].includes(error.code)) return null;
                throw error;
            }
        }
    });
    const relatedEnabled = enabled && !!dossier.data;
    const rates = useQuery({
        queryKey: companyKeys.rates(viewer, id, vehicleId),
        enabled: relatedEnabled,
        retry: false,
        queryFn: ()=>registryRecordClient.getCompanyRateBook({
                companyId: id,
                vehicleId,
                includeUnmapped: true
            })
    });
    const people = useQuery({
        queryKey: companyKeys.people(viewer, id),
        enabled: relatedEnabled,
        retry: false,
        queryFn: ()=>registryRecordClient.listOrganizationPeople({
                employerKind: 'COMPANY',
                employerId: id,
                includePast: true
            })
    });
    return {
        dossier,
        rates,
        people
    };
}
function useViewer() {
    const { user } = useAuth();
    return {
        viewer: `${user?.tenant_id ?? ''}:${user?.id ?? ''}`,
        ready: !!user?.tenant_id && !!user?.id
    };
}
/** The corporate family around a company (REG-62). asOf "YYYY-MM-DD" or "" for today. */ export function useCompanyFamily(id, asOf = '', includeEnded = false) {
    const { viewer, ready } = useViewer();
    return useQuery({
        queryKey: companyKeys.family(viewer, id, asOf, includeEnded),
        enabled: ready && !!id,
        retry: false,
        queryFn: ()=>registryRecordClient.getCompanyFamily({
                companyId: id,
                asOf: dayToTimestamp(asOf),
                includeEnded
            })
    });
}
/** Every stored link touching the company, with notes, headcount and source (for editing). */ export function useCompanyLinks(id) {
    const { viewer, ready } = useViewer();
    return useQuery({
        queryKey: companyKeys.links(viewer, id),
        enabled: ready && !!id,
        retry: false,
        queryFn: async ()=>(await deliveryClient.listCompanyRelationships({
                companyId: '',
                companyProfileId: id
            })).relationships
    });
}
function relationshipBody(link, f, id = '') {
    return {
        id,
        ...link,
        effectiveStart: dayToTimestamp(f.effectiveDate),
        effectiveEnd: dayToTimestamp(f.endDate),
        notes: f.notes.trim(),
        sourceArtifactId: f.sourceArtifactId,
        ...f.transferredHeadcount !== undefined ? {
            transferredHeadcount: f.transferredHeadcount
        } : {}
    };
}
function useInvalidateCompany() {
    const qc = useQueryClient();
    return ()=>{
        void qc.invalidateQueries({
            queryKey: [
                'registry',
                'company'
            ]
        });
        void qc.invalidateQueries({
            queryKey: [
                'company lineage'
            ]
        });
    };
}
/**
 * Record one event: one row per link (a JV with two members is two rows). Rows
 * already written stay if a later one is refused; the error says how many.
 */ export function useRecordCorporateEvent() {
    const invalidate = useInvalidateCompany();
    return useMutation({
        mutationFn: async ({ links, fields })=>{
            let done = 0;
            for (const link of links){
                try {
                    await deliveryClient.createCompanyRelationship({
                        companyId: '',
                        relationship: relationshipBody(link, fields)
                    });
                    done++;
                } catch (err) {
                    const msg = err instanceof ConnectError ? err.rawMessage : String(err);
                    throw new Error(links.length > 1 ? `${done} of ${links.length} recorded: ${msg}` : msg);
                }
            }
        },
        onSuccess: invalidate,
        onError: (err)=>toast.error(err.message)
    });
}
/** Edit or end a stored link; every field is replaced, so pass the whole row. */ export function useUpdateCorporateEvent() {
    const invalidate = useInvalidateCompany();
    return useMutation({
        mutationFn: async ({ row, fields })=>{
            try {
                await deliveryClient.updateCompanyRelationship({
                    companyId: '',
                    relationship: relationshipBody({
                        relationshipType: row.relationshipType,
                        fromCompanyId: row.fromCompanyId,
                        toCompanyId: row.toCompanyId
                    }, fields, row.id)
                });
            } catch (err) {
                throw new Error(err instanceof ConnectError ? err.rawMessage : String(err));
            }
        },
        onSuccess: invalidate,
        onError: (err)=>toast.error(err.message)
    });
}
export function useDeleteCorporateEvent() {
    const invalidate = useInvalidateCompany();
    return useMutation({
        mutationFn: async (relationshipId)=>{
            try {
                await deliveryClient.deleteCompanyRelationship({
                    companyId: '',
                    relationshipId
                });
            } catch (err) {
                throw new Error(err instanceof ConnectError ? err.rawMessage : String(err));
            }
        },
        onSuccess: invalidate,
        onError: (err)=>toast.error(err.message)
    });
}
function useInvalidateCompanyAndLists() {
    const qc = useQueryClient();
    const invalidate = useInvalidateCompany();
    return ()=>{
        invalidate();
        invalidateRegistryLists(qc);
    };
}
const plain = (err)=>new Error(err instanceof ConnectError ? err.rawMessage : String(err));
/** Mark a company as a partner (one open partnership per company). */ export function useMarkPartner() {
    const invalidate = useInvalidateCompanyAndLists();
    return useMutation({
        mutationFn: async ({ companyId, fields })=>{
            try {
                return await registryRecordClient.createCompanyDesignation({
                    companyId,
                    kind: 'PARTNER',
                    effectiveStart: dayToTimestamp(fields.since),
                    notes: fields.notes,
                    sourceArtifactId: fields.sourceArtifactId
                });
            } catch (err) {
                throw plain(err);
            }
        },
        onSuccess: invalidate,
        onError: (err)=>toast.error(err.message)
    });
}
/** Edit a partnership, or end it (it stays as "former partner"). */ export function useUpdatePartner() {
    const invalidate = useInvalidateCompanyAndLists();
    return useMutation({
        mutationFn: async ({ id, fields })=>{
            try {
                return await registryRecordClient.updateCompanyDesignation({
                    id,
                    effectiveStart: dayToTimestamp(fields.since),
                    effectiveEnd: dayToTimestamp(fields.ended),
                    notes: fields.notes,
                    sourceArtifactId: fields.sourceArtifactId
                });
            } catch (err) {
                throw plain(err);
            }
        },
        onSuccess: invalidate,
        onError: (err)=>toast.error(err.message)
    });
}
/** Remove a partnership recorded by mistake. */ export function useRemovePartner() {
    const invalidate = useInvalidateCompanyAndLists();
    return useMutation({
        mutationFn: async (id)=>{
            try {
                await registryRecordClient.deleteCompanyDesignation({
                    id
                });
            } catch (err) {
                throw plain(err);
            }
        },
        onSuccess: invalidate,
        onError: (err)=>toast.error(err.message)
    });
}
